import React, { useState } from 'react';
import PublicLayout from '../components/public/PublicLayout';
import SEO from '../components/common/SEO';
import { seoConfig } from '../config/seoConfig';
import { HandHeart, Users, Calendar, MapPin, Mail, Phone, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ARCA_COLORS = {
  primary: '#6B8E23',
  secondary: '#2C5234',
  light: '#F8F9FA',
  dark: '#212529'
};

const VolunteerPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    profession: '',
    interests: [],
    availability: '',
    experience: '',
    motivation: ''
  });

  const volunteerAreas = [
    'Education & Training',
    'Research & Documentation',
    'Event Organization',
    'Community Outreach',
    'Marketing & Communications',
    'IT & Technology',
    'Fundraising',
    'Translation Services'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInterestToggle = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${API_URL}/communication/volunteer/apply`, formData);
      
      if (response.data.success) {
        toast.success('Thank you for your interest in volunteering! We will review your application and contact you soon. Check your email for confirmation.');
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          country: '',
          city: '',
          profession: '',
          interests: [],
          availability: '',
          experience: '',
          motivation: ''
        });
      }
    } catch (error) {
      console.error('Error submitting volunteer application:', error);
      toast.error(error.response?.data?.message || 'Failed to submit application. Please try again.');
    }
  };

  return (
    <PublicLayout>
      <SEO 
        title={seoConfig.volunteer.title}
        description={seoConfig.volunteer.description}
        keywords={seoConfig.volunteer.keywords}
        url={seoConfig.volunteer.url}
      />
      {/* Hero Section */}
      <section className="position-relative text-white py-3 py-md-4" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)' }}>
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <HandHeart className="mb-2" size={36} />
              <h1 className="h3 h-md-2 fw-bold mb-2">Volunteer with ARCA</h1>
              <p className="mb-0 small">
                Join our community of dedicated volunteers making a difference in respiratory care across Africa.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Volunteer Section */}
      <section className="py-4 py-md-5 bg-white">
        <div className="container">
          <div className="row justify-content-center text-center mb-4">
            <div className="col-lg-8">
              <h2 className="h4 h-md-3 fw-bold text-dark mb-3">Why Volunteer with ARCA?</h2>
              <p className="text-muted">Make a meaningful impact while developing your skills and network</p>
            </div>
          </div>
          
          <div className="row g-3 g-md-4">
            <div className="col-12 col-sm-6 col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4 text-center">
                  <Users style={{ color: ARCA_COLORS.primary }} className="mb-3" size={48} />
                  <h5 className="fw-bold mb-2">Build Your Network</h5>
                  <p className="text-muted mb-0">Connect with healthcare professionals and organizations across Africa</p>
                </div>
              </div>
            </div>
            <div className="col-12 col-sm-6 col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4 text-center">
                  <CheckCircle style={{ color: ARCA_COLORS.secondary }} className="mb-3" size={48} />
                  <h5 className="fw-bold mb-2">Gain Experience</h5>
                  <p className="text-muted mb-0">Develop valuable skills in healthcare, education, and project management</p>
                </div>
              </div>
            </div>
            <div className="col-12 col-sm-6 col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4 text-center">
                  <HandHeart style={{ color: ARCA_COLORS.primary }} className="mb-3" size={48} />
                  <h5 className="fw-bold mb-2">Make an Impact</h5>
                  <p className="text-muted mb-0">Contribute to improving respiratory care and saving lives</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Volunteer Application Form */}
      <section className="py-4 py-md-5 bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="card shadow-lg border-0">
                <div className="card-body p-3 p-md-4 p-lg-5">
                  <h2 className="h4 h-md-3 fw-bold text-center mb-4">Volunteer Application</h2>
                  
                  <form onSubmit={handleSubmit}>
                    {/* Personal Information */}
                    <h5 className="fw-bold mb-3">Personal Information</h5>
                    <div className="row g-3 mb-4">
                      <div className="col-md-6">
                        <label className="form-label">First Name *</label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          className="form-control"
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Last Name *</label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          className="form-control"
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Email *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="form-control"
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Phone *</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="form-control"
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Country *</label>
                        <input
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          className="form-control"
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">City *</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          className="form-control"
                          required
                        />
                      </div>
                    </div>

                    {/* Professional Background */}
                    <h5 className="fw-bold mb-3">Professional Background</h5>
                    <div className="mb-4">
                      <label className="form-label">Profession/Field of Study *</label>
                      <input
                        type="text"
                        name="profession"
                        value={formData.profession}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="e.g., Respiratory Therapist, Medical Student, Nurse"
                        required
                      />
                    </div>

                    {/* Areas of Interest */}
                    <div className="mb-4">
                      <label className="form-label fw-bold">Areas of Interest * (Select all that apply)</label>
                      <div className="row g-2">
                        {volunteerAreas.map((area, index) => (
                          <div key={index} className="col-md-6">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`interest-${index}`}
                                checked={formData.interests.includes(area)}
                                onChange={() => handleInterestToggle(area)}
                              />
                              <label className="form-check-label" htmlFor={`interest-${index}`}>
                                {area}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Availability */}
                    <div className="mb-4">
                      <label className="form-label">Availability *</label>
                      <select
                        name="availability"
                        value={formData.availability}
                        onChange={handleChange}
                        className="form-select"
                        required
                      >
                        <option value="">Select your availability</option>
                        <option value="full-time">Full-time (30+ hours/week)</option>
                        <option value="part-time">Part-time (15-30 hours/week)</option>
                        <option value="flexible">Flexible (Less than 15 hours/week)</option>
                        <option value="project-based">Project-based</option>
                        <option value="event-only">Event-only</option>
                      </select>
                    </div>

                    {/* Experience */}
                    <div className="mb-4">
                      <label className="form-label">Relevant Experience</label>
                      <textarea
                        name="experience"
                        value={formData.experience}
                        onChange={handleChange}
                        className="form-control"
                        rows="4"
                        placeholder="Tell us about your relevant experience, skills, or previous volunteer work..."
                      />
                    </div>

                    {/* Motivation */}
                    <div className="mb-4">
                      <label className="form-label">Why do you want to volunteer with ARCA? *</label>
                      <textarea
                        name="motivation"
                        value={formData.motivation}
                        onChange={handleChange}
                        className="form-control"
                        rows="4"
                        placeholder="Share your motivation for joining ARCA as a volunteer..."
                        required
                      />
                    </div>

                    {/* Submit Button */}
                    <button 
                      type="submit" 
                      className="btn btn-lg w-100 fw-bold text-white"
                      style={{ backgroundColor: ARCA_COLORS.primary }}
                    >
                      <HandHeart size={20} className="me-2" />
                      Submit Application
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Internship Opportunity Section */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm" style={{ borderLeft: `4px solid ${ARCA_COLORS.primary}` }}>
                <div className="card-body p-4">
                  <h3 className="fw-bold mb-3" style={{ color: ARCA_COLORS.primary }}>
                    <Calendar className="me-2" size={28} />
                    Internship Opportunities
                  </h3>
                  <p className="mb-3">
                    ARCA also offers structured internship programs for students and recent graduates 
                    interested in gaining hands-on experience in respiratory care, public health, 
                    healthcare management, or related fields.
                  </p>
                  <p className="mb-4">
                    Internships typically last 3-6 months and provide mentorship, training, and 
                    opportunities to work on meaningful projects.
                  </p>
                  <div className="d-flex flex-column gap-2 text-muted">
                    <div className="d-flex align-items-center">
                      <Mail size={18} className="me-2" style={{ color: ARCA_COLORS.primary }} />
                      <span>For internship inquiries: <a href="mailto:info@africaresp.org" style={{ color: ARCA_COLORS.primary }}>info@africaresp.org</a></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default VolunteerPage;
